import {
  Attributes,
  AttributesRow,
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  closeDialog,
  DialogFooter,
  openDialog,
  Separator,
} from 'platform/components';
import {Grid, GridItem, Heading, HStack, VStack} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, PriceListRowData, refreshDmsDatagrid, usePermissions} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {ArticleCreate} from '../ArticleCreate/ArticleCreate';
import {PriceListDetailAdditionalInformation} from './components/PriceListDetailAdditionalInformation';
import {PriceListDetailPrice} from './components/PriceListDetailPrice';

interface PriceListDetailProps extends RequiredTestIdProps {
  id: string;
  priceListRowData: PriceListRowData;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function PriceListDetail(props: PriceListDetailProps) {
  const navigate = useNavigate();

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const catalogNumber = props?.priceListRowData?.catalogNumber?.value;
  const manufacturer = props?.priceListRowData?.manufacturerId?.value?.id;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([
        filter,
        {
          catalogNumber,
          manufacturer,
          manufacturerNumber: undefined,
          articleId: undefined,
        },
      ]),
    [catalogNumber, manufacturer]
  );

  const handleCreateNewArticle = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber,
          manufacturerId: manufacturer,
          name: props?.priceListRowData?.name?.value,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
              })
            );
          }

          closeCurrentDialog();
        }}
        onClose={() => closeDialog('createNewArticleDialog')}
        hasCreateAndEditButton
        data-testid={suffixTestId('createNewArticle', props)}
      />,
      {
        id: 'createNewArticleDialog',
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        scrollBehavior: 'outside',
        withAdditionalFooter: true,
      }
    );
  };

  const itemsAttributes: AttributesRow[] = [
    {label: i18n.t('entity.warehouse.labels.name'), value: props.priceListRowData?.name?.value},
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      value: props.priceListRowData?.catalogNumber?.value,
    },
    {
      label: i18n.t('entity.warehouse.labels.orderingNumber'),
      value: props.priceListRowData?.supplierOrderingNumber?.value,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: props.priceListRowData?.manufacturerId?.value?.label,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: props.priceListRowData?.supplierId?.value?.label,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplierDiscountGroup'),
      value: props.priceListRowData?.supplierDiscountGroup,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplierManufacturerName'),
      value: props.priceListRowData?.supplierManufacturerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplierManufacturerCode'),
      value: props.priceListRowData?.supplierManufacturerCode,
    },
    {
      label: i18n.t('entity.warehouse.labels.bulkPackageQuantity'),
      value: props.priceListRowData?.standardOrderQuantity,
    },
    {
      label: i18n.t('entity.warehouse.labels.localAvailability'),
      value: props.priceListRowData?.localStock,
    },
    {
      label: i18n.t('entity.warehouse.labels.totalAvailability'),
      value: props.priceListRowData?.totalStock,
    },
  ];

  return (
    <VStack spacing={4}>
      <Card
        variant="inlineGrey"
        title={i18n.t('entity.warehouse.labels.itemDetail')}
        data-testid={suffixTestId('itemDetail', props)}
      >
        <VStack>
          <Attributes
            rows={itemsAttributes}
            size="quarter"
            data-testid={suffixTestId('itemAttributes', props)}
          />

          <Separator />

          <Grid columns={4}>
            <GridItem span={1}>
              <PriceListDetailPrice
                label={`${i18n.t('entity.warehouse.labels.purchasePrice')} ${i18n.t('general.labels.w/oVat')}`}
                price={props.priceListRowData?.purchasePrice?.value?.amount}
                currency={props.priceListRowData?.purchasePrice?.value?.currency}
                data-testid={suffixTestId('prices.purchase', props)}
              />
            </GridItem>
            <GridItem span={1}>
              <PriceListDetailPrice
                label={`${i18n.t('entity.warehouse.labels.recommendedPrice')} ${i18n.t('general.labels.w/oVat')}`}
                price={props.priceListRowData?.recommendedPrice?.value?.amount}
                currency={props.priceListRowData?.purchasePrice?.value?.currency}
                data-testid={suffixTestId('prices.recommended', props)}
              />
            </GridItem>
          </Grid>
        </VStack>
      </Card>

      <PriceListDetailAdditionalInformation
        searchCriteria={{
          catalogNumber,
          manufacturerId: manufacturer,
          supplierId: props.priceListRowData?.supplierId?.value?.id,
          supplierAccountCode: props.priceListRowData?.supplierAccountCode,
          supplierOrderTypeCode: props.priceListRowData?.supplierOrderTypeCode,
          branchId: props.priceListRowData?.branchId,
          authorizationProfileId: props.priceListRowData?.authorizationProfileId,
          additionalInformation: true,
        }}
        data-testid={suffixTestId('additionalInformation', props)}
      />

      <Heading size={5}>{i18n.t('entity.warehouse.labels.availability')}</Heading>

      <DmsDataGrid
        gridCode="warehouse-availability"
        autoHeight
        queryModifier={queryModifier}
        data-testid={suffixTestId('warehouseAvailability', props)}
      />

      <HStack>
        <Button
          variant="link"
          title={i18n.t('general.actions.refresh')}
          leftIcon="navigation/refresh"
          onClick={() => refreshDmsDatagrid('warehouse-availability')}
          data-testid={suffixTestId('actions.refresh', props)}
        />
      </HStack>

      <DialogFooter>
        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('entity.warehouse.actions.createNewArticle'),
              variant: 'secondary',
              onClick: handleCreateNewArticle,
              isDisabled: !articleCreate,
              tooltip: !articleCreate
                ? i18n.t('module.warehouse.label.missingArticleCreatePermission')
                : undefined,
              'data-testid': suffixTestId('actions.createNewArticle', props),
            },
            {
              title: i18n.t('general.actions.close'),
              variant: 'primary',
              onClick: closeCurrentDialog,
              'data-testid': suffixTestId('actions.close', props),
            },
          ]}
        />
      </DialogFooter>
    </VStack>
  );
}
